import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { toast } from 'react-toastify';
import supplierService, {
  computeInvoiceTotals,
  PurchaseInvoice,
  PurchaseSummary,
  Supplier,
} from '../../services/supplierService';
import { toPersianDigitsStr } from '../../utils/persianNumber';
import JalaliDateField from '../common/JalaliDateField';
import BrandProfitTable from './BrandProfitTable';
import InvoiceDetailDrawer from './InvoiceDetailDrawer';
import InvoiceFormDialog from './InvoiceFormDialog';
import { faNum, faPercent, faToman, invoiceDateLabel, marginOf, supplierName } from './format';

interface Props {
  suppliers: Supplier[];
  supplierFilter: string;
  onSupplierFilterChange: (id: string) => void;
  canWrite: boolean;
  onInvoiceCreated?: () => void;
}

const PAGE_SIZE = 20;

const SummaryCard: React.FC<{ label: string; value: string; hint?: string; color?: string }> = ({ label, value, hint, color }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 200px', minWidth: 180 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={800} sx={{ color, mt: 0.5 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Paper>
);

const InvoicesTab: React.FC<Props> = ({ suppliers, supplierFilter, onSupplierFilterChange, canWrite, onInvoiceCreated }) => {
  const [rows, setRows] = useState<PurchaseInvoice[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [summary, setSummary] = useState<PurchaseSummary | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const supplierById = useMemo(() => Object.fromEntries(suppliers.map((s) => [s._id, s])), [suppliers]);

  const filters = useMemo(
    () => ({ supplier: supplierFilter || undefined, from: from || undefined, to: to || undefined }),
    [supplierFilter, from, to],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        supplierService.listInvoices({ ...filters, search: search.trim() || undefined, page, limit: PAGE_SIZE }),
        supplierService.getSummary(filters).catch(() => null),
      ]);
      setRows(list.items);
      setPages(list.pages);
      setTotal(list.total);
      setSummary(sum);
    } catch (e: any) {
      toast.error(e?.message || 'خطا در دریافت فاکتورها');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters, search, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Reset to first page whenever filters change
  useEffect(() => {
    setPage(1);
  }, [filters, search]);

  const hasFilters = !!(supplierFilter || from || to || search);

  return (
    <Box>
      {/* Summary strip */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
        <SummaryCard
          label="کل خرید (بهای تمام‌شده)"
          value={summary ? faToman(summary.totalCostToman) : '—'}
          hint={summary ? `${faNum(summary.invoiceCount)} فاکتور` : undefined}
        />
        <SummaryCard label="کل فروش بالقوه" value={summary ? faToman(summary.totalSaleToman) : '—'} />
        <SummaryCard
          label="سود بالقوه"
          value={summary ? faToman(summary.potentialProfitToman) : '—'}
          hint={summary ? `حاشیه ${faPercent(marginOf(summary.totalCostToman, summary.totalSaleToman))}` : undefined}
          color={summary && summary.potentialProfitToman < 0 ? 'error.main' : 'success.main'}
        />
        <SummaryCard
          label="تعداد اقلام"
          value={summary ? faNum(summary.totalQuantity) : '—'}
          hint={summary?.lineCount !== undefined ? `${faNum(summary.lineCount)} ردیف کالا` : undefined}
        />
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end', mb: 2 }}>
        <TextField
          size="small"
          placeholder="شماره فاکتور…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="تأمین‌کننده"
          value={supplierFilter}
          onChange={(e) => onSupplierFilterChange(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">همه تأمین‌کنندگان</MenuItem>
          {suppliers.map((s) => (
            <MenuItem key={s._id} value={s._id}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
        <JalaliDateField label="از تاریخ" value={from} onChange={setFrom} width={160} height={40} />
        <JalaliDateField label="تا تاریخ" value={to} onChange={setTo} width={160} height={40} />
        {hasFilters && (
          <Tooltip title="حذف فیلترها">
            <IconButton
              onClick={() => {
                setSearch('');
                setFrom('');
                setTo('');
                onSupplierFilterChange('');
              }}
            >
              <FilterAltOffIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="بارگذاری مجدد">
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {canWrite && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            ثبت فاکتور خرید
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>شماره فاکتور</TableCell>
              <TableCell>تأمین‌کننده</TableCell>
              <TableCell>تاریخ</TableCell>
              <TableCell align="center">ردیف کالا</TableCell>
              <TableCell align="center">تعداد اقلام</TableCell>
              <TableCell>بهای تمام‌شده</TableCell>
              <TableCell>فروش بالقوه</TableCell>
              <TableCell>سود بالقوه</TableCell>
              <TableCell align="center">حاشیه</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">فاکتوری یافت نشد</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((inv) => {
                const t = inv.totals && inv.totals.quantity ? inv.totals : computeInvoiceTotals(inv.lines || []);
                const profit = t.saleToman - t.costToman;
                return (
                  <TableRow key={inv._id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedId(inv._id)}>
                    <TableCell sx={{ fontWeight: 600 }}>{toPersianDigitsStr(inv.invoiceNumber || '—')}</TableCell>
                    <TableCell>{supplierName(inv.supplier, supplierById)}</TableCell>
                    <TableCell>{invoiceDateLabel(inv)}</TableCell>
                    <TableCell align="center">{faNum(inv.lineCount ?? inv.lines?.length ?? 0)}</TableCell>
                    <TableCell align="center">{faNum(t.quantity)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{faToman(t.costToman)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{faToman(t.saleToman)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: profit >= 0 ? 'success.main' : 'error.main' }}>
                      {faToman(profit)}
                    </TableCell>
                    <TableCell align="center">{faPercent(marginOf(t.costToman, t.saleToman))}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
          <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
          <Typography variant="caption" color="text.secondary">
            {faNum(total)} فاکتور
          </Typography>
        </Box>
      )}

      <BrandProfitTable rows={summary?.byBrand || []} />

      <InvoiceDetailDrawer invoiceId={selectedId} onClose={() => setSelectedId(null)} />

      {canWrite && (
        <InvoiceFormDialog
          open={createOpen}
          suppliers={suppliers}
          defaultSupplierId={supplierFilter}
          onClose={() => setCreateOpen(false)}
          onSaved={(inv) => {
            setCreateOpen(false);
            load();
            onInvoiceCreated?.();
            if (inv?._id) setSelectedId(inv._id);
          }}
        />
      )}
    </Box>
  );
};

export default InvoicesTab;
